import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { splitJobsByStatus } from "@/app/handlers/jobStatus";
import { API } from "@/app/services/apiService";
import { JobListingItemJob } from "@/app/Views/JobsPage/components/JobListingItem";

/** A search result, carrying the employer status flag the open/closed split reads. */
export type SimilarJob = JobListingItemJob & { jobStatus?: string };

/** The jobs search returns its results grouped by employer, with the company described once. */
interface SearchResultGroup {
	company?: string;
	companyDetails?: {
		_id?: string;
		company_name?: string;
		logo_url?: string;
	};
	jobs?: SimilarJob[];
}

interface UseSimilarJobsParams {
	/** The category to match on. Nothing is fetched without one. */
	category?: string;
	/** The posting being read, kept out of its own list of similar roles. */
	currentJobId?: string;
	/** The employer being read. Their other roles are listed by their own section instead. */
	companyId?: string;
}

interface UseSimilarJobsReturn {
	roles: SimilarJob[];
	isLoading: boolean;
}

/**
 * The search parameters a category browse uses.
 *
 * Built key-for-key like prefetchJobsByCategory so both land on the same React Query entry —
 * hovering a category, opening this section, and browsing /jobs all share one fetch.
 */
function buildCategoryParams(category: string) {
	return {
		title: "",
		country: "Tanzania",
		region: "",
		city: "",
		jobExpertise: [category],
		pageNumber: 1,
	};
}

/**
 * Open roles in the same category as the one being read, from other employers.
 *
 * Category is the only similarity signal the jobs API exposes, and it is the same one the
 * "explore more roles" links have always used — this turns that link into real listings.
 */
export function useSimilarJobs({ category, currentJobId, companyId }: UseSimilarJobsParams): UseSimilarJobsReturn {
	const params = useMemo(() => (category ? buildCategoryParams(category) : null), [category]);

	const { data: response, isLoading } = useQuery({
		queryKey: ["jobs", JSON.stringify(params)],
		queryFn: async () => await API.get("jobs", { params }),
		staleTime: 60 * 1000 * 60,
		retry: 0,
		enabled: Boolean(params),
	});

	const roles = useMemo(() => {
		const groups: SearchResultGroup[] = response?.data?.[0]?.paginatedJobs ?? [];

		// The employer is described once per group rather than on each listing, so it has to be
		// pushed back down before a row can name it — JobListingItem drops a job without one.
		const listings = groups.flatMap((group) => {
			const groupCompany = {
				_id: group.companyDetails?._id || group.company,
				company_name: group.companyDetails?.company_name,
				logo_url: group.companyDetails?.logo_url,
			};

			return (group.jobs ?? []).flatMap((job) => {
				const company = job.company?.company_name ? job.company : groupCompany;
				if (!company?.company_name) return [];

				return [{ ...job, company, logo: company.logo_url }];
			});
		});

		// Same open/closed rule as every other list, so this section cannot advertise a role the
		// job page itself reports as expired.
		return splitJobsByStatus(listings).openJobs.filter(
			(job) => job._id !== currentJobId && job.company?._id !== companyId,
		);
	}, [response, currentJobId, companyId]);

	return { roles, isLoading };
}
